import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { UserRole } from '@/types/enums';
import { RbacAction, defaultAllowed } from '@/lib/rbac';
import rbacService from '@/services/rbacService';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Menyediakan `can(resource, action)` berbasis matriks RBAC dinamis (DB),
 * dengan fallback ke default kode bila belum termuat.
 *
 * Hanya lapisan UX — keamanan sebenarnya tetap di RLS Postgres.
 */
interface PermissionsContextValue {
  /** Apakah role pengguna saat ini boleh melakukan aksi pada resource. */
  can: (resource: string, action?: RbacAction) => boolean;
  loading: boolean;
  /** Muat ulang permission (mis. setelah Super Admin menyimpan perubahan). */
  refresh: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { effectiveRole } = useAuth();
  // role bisa berupa UserRole enum atau string kustom (role tambahan).
  // Pakai effectiveRole agar staf di Mode Nasabah mendapat matriks izin
  // NASABAH (menu/tab tampilan nasabah lolos filter `can`).
  const role = effectiveRole as string | undefined;
  const [allowedMap, setAllowedMap] = useState<Map<string, boolean> | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!role) {
      setAllowedMap(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const map = await rbacService.getRoleMatrix(role);
      setAllowedMap(map);
    } catch {
      // Gagal memuat → biarkan null agar fallback default dipakai.
      setAllowedMap(null);
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { load(); }, [load]);

  const can = useCallback(
    (resource: string, action: RbacAction = 'view'): boolean => {
      if (!role) return false;
      // Super Admin selalu penuh (cegah lockout, konsisten dgn seed).
      if (role === UserRole.SUPER_ADMIN) return true;

      const key = `${resource}.${action}`;
      // Nilai eksplisit dari DB (true ATAU false) selalu menang — menghormati
      // pengaturan admin, termasuk penolakan eksplisit.
      if (allowedMap && allowedMap.has(key)) return allowedMap.get(key) ?? false;

      // Key tidak ada di DB (resource baru belum di-seed) ATAU matriks gagal dimuat:
      // pakai default kode untuk built-in roles; custom role → tertutup.
      const isBuiltin = Object.values(UserRole).includes(role as UserRole);
      return isBuiltin ? defaultAllowed(role as UserRole, resource, action) : false;
    },
    [role, allowedMap],
  );

  const value = useMemo<PermissionsContextValue>(
    () => ({ can, loading, refresh: load }),
    [can, loading, load],
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export function usePermissions(): PermissionsContextValue {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions harus dipakai di dalam <PermissionsProvider>');
  }
  return ctx;
}

/** Komponen gate untuk merender children hanya bila punya akses. */
export const Can: React.FC<{
  resource: string;
  action?: RbacAction;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({ resource, action = 'view', fallback = null, children }) => {
  const { can } = usePermissions();
  return <>{can(resource, action) ? children : fallback}</>;
};
